import { PROJECT_NAME } from '../project/PROJECT_NAME';
import { PROJECT_YEAR } from '../project/PROJECT_YEAR';
import { PROJECT_COPYRIGHT } from '../project/PROJECT_COPYRIGHT';
import { COMPANY_EMAIL } from '../contact/COMPANY_EMAIL';
import { LEGAL_CONTACTS } from './LEGAL_CONTACTS';
import { BUSINESS_LEGAL_NAME } from './BUSINESS_LEGAL_NAME';
import { LEGAL_DEFINITIONS } from './LEGAL_DEFINITIONS';
import { LEGAL_JURISDICTIONS } from './LEGAL_JURISDICTIONS';

/**
 * @group Legal
 * @summary <blockquote>Comprehensive Privacy Policy content (structured) for use in web/mobile.</blockquote>
 *
 * @remarks
 * This is a structured policy object that can be rendered on web/mobile.
 * It references shared constants (e.g., project name, contacts) instead of hardcoding.
 */
export const PRIVACY_POLICY = {
  metadata: {
    title: `${PROJECT_NAME} Privacy Policy`,
    version: '1.0.0',
    lastUpdated: new Date().toISOString().slice(0, 10),
    copyright: PROJECT_COPYRIGHT,
    businessName: BUSINESS_LEGAL_NAME
  },
  introduction: [
    `${BUSINESS_LEGAL_NAME} ("we", "us", "our") is a South African healthcare technology company committed to protecting the privacy of every South African. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use ${PROJECT_NAME}, in compliance with the Protection of Personal Information Act (POPIA) and other applicable South African data protection laws.`,
    'By using our services, you acknowledge that you have read and understood this Privacy Policy.',
    'We are transparent about how we handle your health data and personal information to ensure you can trust us with your healthcare.'
  ],
  sections: [
    {
      title: '1. Information We Collect',
      content: [
        'We collect information necessary to provide healthcare services and comply with legal requirements:',
        '• Health Information: vital signs, medical history, consultation notes',
        '• Personal Information: name, contact details, demographics',
        '• Device Information: IP address, device type, app usage analytics',
        '• Transaction Information: payment details (processed securely)'
      ]
    },
    {
      title: '2. How We Use Your Information',
      content: [
        'Your information enables us to:',
        '• Deliver healthcare consultations and services',
        '• Process payments and manage subscriptions',
        '• Comply with South African healthcare regulations',
        '• Improve service quality and user experience',
        '• Communicate important health and service updates',
        '• Prevent fraud and ensure system security'
      ]
    },
    {
      title: '3. Data Security & Protection',
      content: [
        'We implement enterprise-grade security measures:',
        '• AES-256 encryption for all data transmission and storage',
        '• ISO 27001 information security management',
        '• Regular security audits and penetration testing',
        '• Strict access controls and role-based permissions',
        '• POPIA compliance and data protection frameworks'
      ]
    },
    {
      title: '4. Your Rights Under POPIA',
      content: [
        'You have the right to:',
        '• Access your personal information',
        '• Request correction of inaccurate data',
        '• Request deletion of your information',
        '• Object to processing of your data',
        '• Request restriction of processing',
        `• Contact our Information Officer: ${LEGAL_CONTACTS.dataProtectionOfficerEmail}`
      ]
    },
    {
      title: '5. Data Retention',
      content: [
        'We retain your data for as long as necessary to provide services and comply with legal obligations. Health records are retained in accordance with South African healthcare standards. You may request deletion subject to legal requirements.'
      ]
    },
    {
      title: '6. Third-Party Sharing',
      content: [
        'We share information only when necessary:',
        '• Healthcare practitioners involved in your care',
        '• Payment processors (securely, with encryption)',
        '• Regulatory bodies (as required by law)',
        '• Service providers under strict data processing agreements',
        'We never sell your data to third parties.'
      ]
    },
    {
      title: '7. International Transfers',
      content: [
        'Any international data transfers comply with POPIA requirements and include appropriate safeguards such as Standard Contractual Clauses.'
      ]
    },
    {
      title: '8. Cookies & Tracking',
      content: [
        'We use cookies to enhance your experience. You can control cookie preferences in your browser settings. We do not track you for marketing purposes without consent.'
      ]
    },
    {
      title: '9. Policy Updates',
      content: [
        `This policy was last updated on ${new Date().toISOString().slice(0, 10)}. We reserve the right to update it. Continued use of ${PROJECT_NAME} after updates constitutes acceptance of the new policy.`
      ]
    },
    {
      title: '10. Contact Us',
      content: [
        'For privacy inquiries or concerns:',
        `Email: ${COMPANY_EMAIL}`,
        `Information Officer: ${LEGAL_CONTACTS.dataProtectionOfficerEmail}`
      ]
    },
    {
      title: '11. Data Deletion',
      content: [
        'You have the right to request deletion of your personal data at any time.',
        'To request deletion, visit our data deletion page at /delete-data on our website.',
        'After submitting your request, you will receive a confirmation email within a few minutes.',
        'Click the link in the email to confirm and complete the deletion process.',
        'Once confirmed, all your data will be permanently deleted within 30 days.',
        'This includes: account information, patient profile, payment methods, health records, and consultation history.',
        'Note: Some data may be retained if required by South African healthcare regulations (HPCSA requirements).',
        'For questions about data deletion, contact our support team.',
      ]
    }
  ],
  jurisdictions: LEGAL_JURISDICTIONS,
  definitions: LEGAL_DEFINITIONS
};
